import { createQueryKeys } from "@lukemorales/query-key-factory";

import type { CoreApiClient } from "../client";
import type { BoilerRoom, FullBoilerRoom } from "../types/boiler-room.type";

export const createBoilerRoomQueries = (api: CoreApiClient) => {
  const { client } = api;

  const list = async (buildingId: string): Promise<BoilerRoom[]> => {
    const { data, error } = await client.GET(`/buildings/{buildingId}/boiler-rooms`, {
      params: { path: { buildingId } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  const byId = async (boilerRoomId: string): Promise<FullBoilerRoom> => {
    const { data, error } = await client.GET(`/boiler-rooms/{boilerRoomId}`, {
      params: { path: { boilerRoomId } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  /**
   * Live/historical snapshot. No `date` → the backend round-trips the gateway (poll this in Live mode); a
   * `date` (ISO) → the state in effect at that instant, read from the timeseries.
   */
  const state = async (boilerRoomId: string, date?: string): Promise<FullBoilerRoom> => {
    const { data, error } = await client.GET(`/boiler-rooms/{boilerRoomId}/state`, {
      params: { path: { boilerRoomId }, query: date ? { date } : undefined },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  /** Distinct instants (newest first) with a historical sample — the history slider's real data points. */
  const stateDates = async (boilerRoomId: string, startDate: string, endDate: string): Promise<string[]> => {
    const { data, error } = await client.GET(`/boiler-rooms/{boilerRoomId}/state/dates`, {
      params: { path: { boilerRoomId }, query: { startDate, endDate } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  return {
    list,
    byId,
    state,
    stateDates,
    queries: createQueryKeys("boilerRooms", {
      building: (buildingId: string) => ({
        queryKey: [buildingId],
        queryFn: () => list(buildingId),
      }),
      boilerRoom: (boilerRoomId: string) => ({
        queryKey: [boilerRoomId],
        queryFn: () => byId(boilerRoomId),
      }),
      state: (boilerRoomId: string, date?: string) => ({
        queryKey: [boilerRoomId, "state", date ?? "live"],
        queryFn: () => state(boilerRoomId, date),
      }),
      stateDates: (boilerRoomId: string, startDate: string, endDate: string) => ({
        queryKey: [boilerRoomId, "state-dates", startDate, endDate],
        queryFn: () => stateDates(boilerRoomId, startDate, endDate),
      }),
    }),
  };
};
